/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Ticket } from './Ticket';
import { Variance } from './Variance';

import * as firebaseUtils from './firebase-utils';
import { IFirebaseRefLike } from './IRefLike';

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum pinColors {
	blue = '#0B73C0',
	red = '#E44B47',
	black = '#2D2D2D',
	green = '#93d200',
}

//TODO - this
// eslint-disable-next-line @typescript-eslint/naming-convention
export enum pinState {
	missed = 'missed',
	missedEarly = 'missedEarly',
	completedOnTime = 'completedOnTime',
	completedEarly = 'completedEarly',
	completedLate = 'completedLate',
	promised = 'promised',
	late = 'late',
	early = 'early',
}

// eslint-disable-next-line @rushstack/typedef-var
export const prettyStateMap = {
	missed: 'Missed', //<----------------
	missedEarly: 'Missed Early', //<--------
	completedOnTime: 'Finished On Time',
	completedEarly: 'Finished Early',
	completedLate: 'Finished Late',
	promised: 'Pending',
	late: 'Rescheduled Late',
	early: 'Rescheduled Early',
};

// eslint-disable-next-line @rushstack/typedef-var
const pinDbKeys = [
	'plannedStart',
	'plannedFinish',
	'actualFinish',
	'varianceId',
	'varianceComment',
	'pinType',
	'createdAt',
	'createdBy',
];

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface PromisePin {
	$id: string;
	data: PromisePinData;
}
// eslint-disable-next-line @typescript-eslint/naming-convention
export interface PromisePinData {
	plannedStart: string;
	plannedFinish: string;
	finishX?: number;
	actualFinish?: string;
	varianceId?: string;
	varianceComment?: string;
	variance?: Variance;
	pinType?: string;
	color: pinColors;
	state: pinState;
	prettyState: string;
	createdAt?: string;
	createdBy?: string;
}

export function decoratePinData(
	pinData: PromisePinData,
	ticketPlannedFinish: string,
	getDateFromX: (date: string) => number,
	nextPin?: PromisePinData,
	varianceList?: Map<string, Variance>
): boolean {
	const isMostRecent = !nextPin;
	// const combinedNextFinish = nextPin.actualFinish || nextPin.plannedFinish;
	if (!isMostRecent && nextPin.plannedFinish < pinData.plannedFinish) {
		pinData.color = pinColors.blue;
		pinData.state = pinState.missedEarly;
	} else if (!isMostRecent) {
		//missed absorbs the technically possible "missedOnTime"
		pinData.color = pinColors.red;
		pinData.state = pinState.missed;
	} else if (pinData.actualFinish && pinData.actualFinish === pinData.plannedFinish) {
		pinData.color = pinColors.green;
		pinData.state = pinState.completedOnTime;
	} else if (pinData.actualFinish && pinData.actualFinish < pinData.plannedFinish) {
		pinData.color = pinColors.blue;
		pinData.state = pinState.completedEarly;
	} else if (pinData.actualFinish && pinData.actualFinish > pinData.plannedFinish) {
		pinData.color = pinColors.red;
		pinData.state = pinState.completedLate;
	} else if (ticketPlannedFinish === pinData.plannedFinish) {
		pinData.color = pinColors.black;
		pinData.state = pinState.promised;
	} else if (ticketPlannedFinish < pinData.plannedFinish) {
		pinData.color = pinColors.blue;
		pinData.state = pinState.early;
	} else {
		pinData.color = pinColors.red;
		pinData.state = pinState.late;
	}

	if (pinData.varianceId && varianceList) {
		pinData.variance = varianceList.get(pinData.varianceId);
	}

	//apply prettyState
	pinData.prettyState = prettyStateMap[pinData.state];

	const lastX = pinData.finishX;
	pinData.finishX = getDateFromX(pinData.plannedFinish);
	return pinData.finishX !== lastX;
}

export function makePromisePeriodArray(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	stack: { [x: string]: any },
	ticketPlannedFinish: string,
	getDateFromX: (date: string) => number,
	varianceList?: Map<string, Variance>
	// eslint-disable-next-line @rushstack/no-new-null
): PromisePin[] | null {
	if (!stack) {
		return null;
	}
	const stackArr = Object.keys(stack)
		.sort()
		.map((key, idx, arr) => {
			const obj = {
				$id: key,
				data: stack[key] ? Object.assign({}, stack[key]) : stack[key],
			};
			// decoratePinData(obj.data, ticketPlannedFinish, getDateFromX, idx === arr.length-1, varianceList);
			return obj;
		});

	stackArr.forEach((pin, key, arr) => {
		const nextPin = key < arr.length - 1 ? arr[key + 1].data : null;
		decoratePinData(pin.data, ticketPlannedFinish, getDateFromX, nextPin, varianceList);
	});

	return stackArr;
}

export function cloneStack(stack: PromisePin[]): PromisePin[] {
	const newArray = stack.map((pin) => {
		return clonePin(pin);
	});
	return newArray;
}
export function clonePin(pin: PromisePin): PromisePin {
	return {
		$id: pin.$id,
		data: Object.assign({}, pin.data),
	};
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function returnToObject(promiseArray: PromisePin[]) {
	if (!promiseArray) {
		return null;
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const obj: { [key: string]: any } = {};
	promiseArray.forEach((promise) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const innerObj: { [key: string]: any } = {};
		pinDbKeys.forEach((key) => {
			if (promise.data[key as keyof PromisePinData]) {
				innerObj[key] = promise.data[key as keyof PromisePinData];
			}
		});
		obj[promise.$id] = innerObj;
	});
	return obj;
}

/**
 * Creates a new pin in a firebase safe way. In order to make this flexible, accept
 * the desired pin data as an argument, we could _probably_ calculate it internally though.
 * @param  idOrParentRef A firebase ref to call .push on, or an id generated by .push
 * @param  ticket        The ticket the pin should be added to
 * @return               A completely new array intended to
 * be forwarded to liveUpdate to be applied to the ticket
 */
export function createPromisePin(
	idOrParentRef: string | IFirebaseRefLike,
	ticket: Ticket,
	pinData: PromisePinData,
	keyGetter: (ref: IFirebaseRefLike) => string
): PromisePin[] {
	const pin = internalCreatePromisePin(idOrParentRef, ticket, pinData, keyGetter);
	const arr = ticket.view.livePromisePeriodStack ? [...ticket.view.livePromisePeriodStack] : [];
	arr.push(pin);
	return arr;
}
/**
 * Creates a new pin in a firebase safe way. Only returns the new pin data, intended
 * only to be used internally.
 *
 * @param  idOrParentRef A firebase ref to call .push on, or an id generated by .push
 * @param  ticket        The ticket the pin should be added to
 * @return               Returns the new pin data
 */
export function internalCreatePromisePin(
	idOrParentRef: string | IFirebaseRefLike,
	ticket: Ticket,
	pinData: PromisePinData,
	keyGetter: (ref: IFirebaseRefLike) => string
): PromisePin {
	const id = getNewPinId(idOrParentRef, keyGetter);

	return {
		$id: id!,
		data: pinData,
	};
}

/**
 * Builds out some initial promise pin data. Its not technically valid as some generated
 * keys are missing. The intent is that this will pass typescipt bs. Then the data Will
 * get filled in by ticket localUpdate before any "client code" knows about it.
 * @param  plannedStart  start date
 * @param  plannedFinish finish date
 * @param  variance      unlikely to know this, or want to set it
 */
export function createInitialPinData(
	plannedStart: string,
	plannedFinish: string,
	variance?: Variance
): PromisePinData {
	const obj = {
		plannedStart,
		plannedFinish,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any;
	if (variance) {
		obj.variance = variance;
	}
	return obj;
}

/**
 * Accepts a pin and variance info, clones it, and applies the variance info
 * Inputs mimic Ticket.localUpdate. Undefined is "unset" and will be skipped, null means "erase", and a real value is... a real value
 * @return                 Returns the new pin with variance applied
 */
export function applyVarianceToPin(
	pin: PromisePin,
	// eslint-disable-next-line @rushstack/no-new-null
	varianceId?: string | null,
	// eslint-disable-next-line @rushstack/no-new-null
	varianceComment?: string | null
): PromisePin {
	const newPin = clonePin(pin);
	if (varianceId !== undefined) {
		newPin.data.varianceId = varianceId || null!;
	}
	if (varianceComment !== undefined) {
		newPin.data.varianceComment = varianceComment || null!;
	}
	return newPin;
}

function getNewPinId(
	idOrParentRef: string | IFirebaseRefLike,
	keyGetter: (ref: IFirebaseRefLike) => string
	// eslint-disable-next-line @rushstack/no-new-null
): string | null {
	if (typeof idOrParentRef === 'string') {
		return idOrParentRef;
	} else if (firebaseUtils.isRef(idOrParentRef)) {
		return keyGetter(idOrParentRef);
	} else {
		throw new Error("arg isn't string or a ref");
	}
}
